// src/components/property/display/PropertyImageGallery.js
import React, { useMemo, useState } from 'react';
import './PropertyImageGallery.css';
import defaultLogo from '../../../assets/images/logo.png';
import { getFullImageUrl } from '../../../utils/config';
import ImageLightbox from './ImageLightbox';

const PropertyImageGallery = ({ property, mainImageURL, propertyImages }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const { mainImage, additionalImages, allImages, totalImageCount } = useMemo(() => {
    const main = mainImageURL ? getFullImageUrl(mainImageURL) : null;
    const additional = Array.isArray(propertyImages) ? propertyImages.filter((img) => img && img !== mainImageURL).map((img) => getFullImageUrl(img)) : [];
    const all = main ? [main, ...additional] : additional;
    return { mainImage: main, additionalImages: additional, allImages: all, totalImageCount: all.length };
  }, [mainImageURL, propertyImages]);

  const handleImageClick = (image) => {
    const index = allImages.findIndex((img) => img === image);
    if (index > -1) {
      setSelectedImageIndex(index);
    }
  };

  const handleCloseLightbox = () => {
    setSelectedImageIndex(null);
  };

  const handleNext = () => {
    setSelectedImageIndex((prevIndex) => {
      if (prevIndex === null || prevIndex >= allImages.length - 1) return prevIndex;
      return prevIndex + 1;
    });
  };

  const handlePrevious = () => {
    setSelectedImageIndex((prevIndex) => {
      if (prevIndex === null || prevIndex <= 0) return prevIndex;
      return prevIndex - 1;
    });
  };

  const handleViewAllClick = () => {
    // The first image not shown in the main gallery view is the 4th one (index 3)
    if (allImages.length > 3) {
      setSelectedImageIndex(3);
    }
  };

  return (
    <>
      <div className="property-image-gallery" dir="ltr">
        {totalImageCount > 0 ? (
          <div className="gallery-container">
            <div className="gallery-main-image">
              <img
                src={mainImage || defaultLogo}
                alt="Main property view"
                onClick={() => handleImageClick(mainImage)}
                onError={(e) => {
                  e.target.src = defaultLogo;
                }}
              />
              {property?.propertyID && <div className="gallery-property-id">ID: {property.propertyID}</div>}
            </div>
            {additionalImages.length > 0 && (
              <div className="gallery-thumbnails">
                {additionalImages.slice(0, 2).map((image, index) => (
                  <div className="gallery-thumbnail-item" key={index}>
                    <img
                      src={image}
                      alt={`Property thumbnail ${index + 1}`}
                      onClick={() => handleImageClick(image)}
                      onError={(e) => {
                        e.target.src = defaultLogo;
                      }}
                    />
                  </div>
                ))}
                {totalImageCount > 3 && (
                  <button className="view-all-photos-btn" onClick={handleViewAllClick}>
                    View all {totalImageCount} photos
                  </button>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="gallery-placeholder">
            <img src={defaultLogo} alt="Default property view" className="gallery-placeholder-image" />
            {property?.propertyID && <div className="gallery-property-id">ID: {property.propertyID}</div>}
          </div>
        )}
      </div>
      {selectedImageIndex !== null && (
        <ImageLightbox
          src={allImages[selectedImageIndex]}
          onClose={handleCloseLightbox}
          onNext={handleNext}
          onPrevious={handlePrevious}
          hasNext={selectedImageIndex < allImages.length - 1}
          hasPrevious={selectedImageIndex > 0}
          currentIndex={selectedImageIndex}
          totalCount={totalImageCount}
        />
      )}
    </>
  );
};

export default PropertyImageGallery;
